import React, { useState, useEffect, useMemo } from "react";
import { getApiFilterKey, formatActualsThroughFromMonths, formatActualsThroughDate, headerActualsDate, appendPeriodFilters } from "./plTimeFilterUtils";
import { toReportApiFilters } from "../filterRelationshipUtils";
import {
  usePlByProgramStyles,
  PlFilterGroup,
  PlSimpleKpiCard,
  PlPageHeader,
  PlChartPanel,
  buildPlDivergingBarOption,
  bridgeContractMetricV1M,
} from "./plByProgramTheme";
import { useReportFonts } from "../../reportFonts";
import { getGMRevBridgeAPI, getVersionsAPI } from "../../../../api/reports-api";
import Loading from "../../../../common-components/Loading";

function buildApiFilters(filters) {
  const f = toReportApiFilters(filters, [
    "year",
    "orgId",
    "contractType",
    "clinLvl2",
    "businessType",
    "awardType",
    "competitionType",
    "popEndDate",
    "bu",
    "sector",
  ]);
  return appendPeriodFilters(f, filters);
}

const fmtM = (v) => {
  if (v == null) return "—";
  const m = v / 1_000_000;
  return m < 0 ? `(${Math.abs(m).toFixed(1)})` : m.toFixed(1);
};

export default function GMRevBridge({ filters, actualsThroughDate }) {
  const classes = usePlByProgramStyles();
  useReportFonts();

  const year =
    filters?.year && filters.year !== "All"
      ? filters.year
      : String(new Date().getFullYear());

  const [versionOptions, setVersionOptions] = useState([]);
  const [version1, setVersion1] = useState("");
  const [version2, setVersion2] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  /* Fetch versions when year changes */
  useEffect(() => {
    getVersionsAPI(year)
      .then((res) => {
        if (res?.data?.length) {
          const opts = res.data.map((v) => ({ value: v.key, label: v.name }));
          setVersionOptions(opts);
          setVersion1(opts[0]?.value ?? "");
          setVersion2(opts[1]?.value ?? opts[0]?.value ?? "");
        }
      })
      .catch(() => {});
  }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  const apiFilterKey = useMemo(
    () => getApiFilterKey(filters, buildApiFilters),
    [filters],
  );

  useEffect(() => {
    if (!version1 || !version2) return;
    setLoading(true);
    getGMRevBridgeAPI(buildApiFilters(filters), version1, version2)
      .then((res) => {
        if (res?.status === 200 && res.data) setData(res.data);
        else setData(null);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [apiFilterKey, version1, version2]); // eslint-disable-line react-hooks/exhaustive-deps

  const v1Name =
    data?.version1?.name ??
    versionOptions.find((o) => o.value === version1)?.label ??
    version1;
  const v2Name =
    data?.version2?.name ??
    versionOptions.find((o) => o.value === version2)?.label ??
    version2;
  const fyLabel = data?.fiscalYear ? `FY-${data.fiscalYear}` : `FY-${year}`;
  const headerDate = headerActualsDate(
    filters?.year,
    loading,
    formatActualsThroughDate(data?.actualThroughMonth),
    formatActualsThroughFromMonths(data?.actualMonths1 ?? data?.actualMonths2),
  );

  const revV1 = data?.kpis?.revenue?.v1 ?? 0;
  const revV2 = data?.kpis?.revenue?.v2 ?? 0;
  const revVariance = revV1 - revV2;
  const gmV1 = data?.kpis?.grossMargin?.v1 ?? 0;
  const gmV2 = data?.kpis?.grossMargin?.v2 ?? 0;
  const gmVariance = gmV1 - gmV2;

  const contracts = data?.contracts ?? [];
  const names = contracts.map((c) => c.contractName || c.contractId);
  const contractIds = contracts.map((c) => c.contractId ?? "");
  const gmVals = contracts.map(
    (c) => +(c.grossMarginVariance / 1_000_000).toFixed(2),
  );
  const revVals = contracts.map(
    (c) => +(c.revenueVariance / 1_000_000).toFixed(2),
  );
  const revAmounts = contracts.map((c) => bridgeContractMetricV1M(c, "revenue"));
  const gmAmounts = contracts.map((c) => bridgeContractMetricV1M(c, "grossMargin"));

  const v1Opts = versionOptions.length
    ? versionOptions
    : [{ value: version1, label: v1Name }];
  const v2Opts = versionOptions.length
    ? versionOptions
    : [{ value: version2, label: v2Name }];

  return (
    <>
      {loading && <Loading />}
      <div className={classes.root}>
        <div className={classes.container}>
          <PlPageHeader
            classes={classes}
            title="GM/Rev - Bridge"
            accent="Variance Analysis"
            meta={`Gross Margin & Revenue Variance · ${v1Name} Vs. ${v2Name}`}
            metaTop={`${fyLabel} · ${v1Name} Vs. ${v2Name}`}
            date={headerDate}
          />

          <div className={classes.sectionCard}>
            <div className={classes.filterBar}>
              <div className={classes.versionCompareRow}>
                <PlFilterGroup
                  label="Version 1"
                  options={v1Opts}
                  value={version1}
                  onChange={setVersion1}
                  classes={classes}
                />
                <span className={classes.versionVs}>vs</span>
                <PlFilterGroup
                  label="Version 2"
                  options={v2Opts}
                  value={version2}
                  onChange={setVersion2}
                  classes={classes}
                />
              </div>
            </div>
            {!loading && (
              <>
                <div className={classes.kpiArea}>
                  <div className={classes.kpiGrid6}>
                    <PlSimpleKpiCard
                      label={`GM ${v1Name}`}
                      value={fmtM(gmV1)}
                      rawValue={gmV1}
                      classes={classes}
                      formatValue={(v) => v}
                    />
                    <PlSimpleKpiCard
                      label={`GM ${v2Name}`}
                      value={fmtM(gmV2)}
                      rawValue={gmV2}
                      classes={classes}
                      formatValue={(v) => v}
                    />
                    <PlSimpleKpiCard
                      label="GM Variance"
                      value={fmtM(gmVariance)}
                      rawValue={gmVariance}
                      classes={classes}
                      formatValue={(v) => v}
                    />
                    <PlSimpleKpiCard
                      label={`Rev ${v1Name}`}
                      value={fmtM(revV1)}
                      rawValue={revV1}
                      classes={classes}
                      formatValue={(v) => v}
                    />
                    <PlSimpleKpiCard
                      label={`Rev ${v2Name}`}
                      value={fmtM(revV2)}
                      rawValue={revV2}
                      classes={classes}
                      formatValue={(v) => v}
                    />
                    <PlSimpleKpiCard
                      label="Revenue Variance"
                      value={fmtM(revVariance)}
                      rawValue={revVariance}
                      classes={classes}
                      formatValue={(v) => v}
                    />
                  </div>
                </div>

                <div className={classes.chartPanelStack}>
                  <PlChartPanel
                    title={`Top 10 & Bottom 10 by Contract Names (Gross Margin) — ${v1Name} Vs. ${v2Name}`}
                    option={buildPlDivergingBarOption({
                      categories: names,
                      values: gmVals,
                      contractIds,
                      metricAmounts: gmAmounts,
                      metricLabel: "Gross Margin",
                    })}
                    classes={classes}
                    panelClassName={classes.chartPanelBridge}
                    bodyClassName={classes.chartPanelBodyBridge}
                  />
                  <PlChartPanel
                    title={`Top 10 & Bottom 10 by Contract Names (Revenue) — ${v1Name} Vs. ${v2Name}`}
                    option={buildPlDivergingBarOption({
                      categories: names,
                      values: revVals,
                      contractIds,
                      metricAmounts: revAmounts,
                      metricLabel: "Revenue",
                    })}
                    classes={classes}
                    panelClassName={classes.chartPanelBridge}
                    bodyClassName={classes.chartPanelBodyBridge}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
